import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import CabinetsApi from "../../../../services/api/cabinetsApi";
import { toast } from "react-toastify";
import { useUser } from "../../../../context/userContext";
import RequestsApi from "../../../../services/api/requestsApi";
import { stepsToTransfer } from "../../../../helpers/adminConstants/AdminConstants";
import { archiveColumns } from "../../../../helpers/adminConstants/TableConstants";
import { PlaceholderImg } from "../../../../components/styled/AllImages";
import { getFormattedDate, getFormattedTime } from "../../../../utils";
import { Box, SubHeadingStyled } from "../../../../components/styled/Elements";
import ListTable from "../../../../components/common/tables/ListTable";
import HistoryModal from "../../../../components/admin/Modals/HistoryModal/HistoryModal";
import TransferModal from "../../cabinets/components/TransferModal";
import DeleteModal from "../../../../components/admin/Modals/DeleteModal/DeleteModal";
import RequestModal from "../../../../components/admin/Modals/RequestModal/RequestModal";

const ASSET_URL = process.env.REACT_APP_ASSET_URL;

const ArchivesList = ({
  loading,
  rowsData,
  activePage,
  setActivePage,
  perPage,
  totalCount,
  getCabinetsList,
  searchParams,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUser();
  const userId = Number(user?.id);
  const userTypeId = Number(user?.user_type_id);
  const { id, fileId } = useParams();
  const [accessDenied, setAccessDenied] = useState(false);
  const [archiveId, setArchiveId] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [transferLoading, setTransferLoading] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [archivesList, setArchivesList] = useState([]);
  const [historyData, setHistoryData] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [currentOwner, setCurrentOwner] = useState(null);
  const [transferSelections, setTransferSelections] = useState(
    Array(stepsToTransfer.length).fill(null)
  );
  const [isTemporary, setIsTemporary] = useState(null);

  const filteredColumns =
    parseInt(user?.user_type_id) === 6
      ? archiveColumns.filter((col) => col.name !== "privacy")
      : archiveColumns;

  const onDelete = (row) => {
    setArchiveId(row?.hash_id);
    setShowDeleteModal(true);
  };
  const handleEdit = (row) => {
    if (row?.is_temporary)
      window.open(
        `/temporary-cabinets/${row?.file_name?.cabinet_name?.hash_id}/temporary-files/${row?.file_name?.hash_id}/temporary-archives/edit-temporary-archive/${row.hash_id}`,
        "_blank"
      );
    else
      window.open(
        `/cabinets/${row?.file_name?.cabinet_name?.hash_id}/files/${row?.file_name?.hash_id}/archives/edit-archive/${row.hash_id}`,
        "_blank"
      );
  };

  const handleInfo = (row) => {
    getHistoryData(row.hash_id);
    setShowHistoryModal(true);
  };
  const handleTransfer = (row) => {
    setArchiveId(row.id);
    setShowTransferModal(true);
  };

  const handleDelete = async () => {
    try {
      const response = await CabinetsApi.deleteArchive(id, fileId, archiveId);
      if (response.code === 200) {
        toast.success(t("archive_deleted"));
        setShowDeleteModal(false);
        setArchiveId(null);
        getCabinetsList();
      }
      if (response.code === 100 || response.code === 204) {
        toast.error(response.message);
      }
    } catch (error) {}
  };

  const onViewDetails = (row) => {
    setArchiveId(row.id);
    const {
      common_settings_id: settingId,
      current_owner,
      current_owner_department_id,
      created_by_user,
      is_temporary,
      file_archive_detail,
      access_granted_requests,
    } = row || {};
    const { department } = user || {};
    const departmentId = Number(department?.id);
    const currentSettingId =
      settingId != 1
        ? settingId
        : row?.file_name?.common_settings_id != 1
        ? row?.file_name?.common_settings_id
        : row?.file_name?.cabinet_name?.setting_id;
    const currentDetails =
      settingId != 1
        ? file_archive_detail
        : row?.file_name?.common_settings_id != 1
        ? row?.file_name?.cabinetfile_detail
        : row?.file_name?.cabinet_name?.cabinet_detail;

    const isLatestOwner = current_owner && userId === Number(current_owner);

    const isUserSelected = currentDetails?.selected_users?.includes(userId);
    const isDepartmentSelected =
      currentDetails?.selected_departments &&
      Array.isArray(currentDetails?.selected_departments)
        ? currentDetails?.selected_departments?.includes(departmentId)
        : currentDetails?.selected_departments &&
          !Array.isArray(currentDetails?.selected_departments) &&
          Number(currentDetails?.selected_departments) === departmentId;

    const isOpenToUser =
      (currentSettingId === 3 &&
        Number(current_owner_department_id) === departmentId) ||
      (currentSettingId === 4 && isUserSelected) ||
      (currentSettingId === 5 && isUserSelected) ||
      (currentSettingId === 6 && isDepartmentSelected) ||
      (currentSettingId === 7 && isDepartmentSelected) ||
      (currentSettingId === 9 && isUserSelected);

    const hasAccessThroughRequest =
      access_granted_requests &&
      Array.isArray(access_granted_requests) &&
      access_granted_requests?.some(
        (item) => Number(item?.from_user_id) === userId
      );

    if (
      isLatestOwner ||
      isOpenToUser ||
      hasAccessThroughRequest ||
      userTypeId === 6
    ) {
      if (is_temporary)
        window.open(
          `/temporary-cabinets/${row?.file_name?.cabinet_name?.hash_id}/temporary-files/${row?.file_name?.hash_id}/temporary-archives/temporary-archive-details/${row.hash_id}`,
          "_blank"
        );
      else
        window.open(
          `/cabinets/${row?.file_name?.cabinet_name?.hash_id}/files/${row?.file_name?.hash_id}/archives/archive-details/${row.hash_id}`,
          "_blank"
        );
    } else {
      setShowRequestModal(true);
    }
  };
  const handleRequest = async () => {
    try {
      setRequestLoading(true);
      const { code, message, error } = await RequestsApi.createRequest({
        module_id: 3,
        module_reference_id: archiveId,
      });
      if (code === 200) {
        toast.success(t("req_sent_successful"));
        setShowRequestModal(false);
      }
      if (code === 100 || code === 204) {
        toast.error(message);
      }
      if (code === 400 && error) {
        toast.error(error);
      }
    } catch (error) {
    } finally {
      setRequestLoading(false);
    }
  };

  const handleStatusToggle = async (row) => {
    const newObj = {
      archive_id: row.id,
      status: parseInt(row.status) === 1 ? 0 : 1,
    };
    try {
      const response = await CabinetsApi.updateArchiveStatus(
        id,
        fileId,
        newObj
      );
      if (response.code === 200) {
        toast.success(
          `${t("archive")} ${t("status")} ${t("Updated_Successfully")}`
        );
        getCabinetsList();
      }
      if (response.code === 204 || response.code === 100) {
        toast.error(response.message);
      }
    } catch (error) {}
  };

  const getHistoryData = async (archiveId) => {
    try {
      setHistoryLoading(true);
      const { data, code } = await CabinetsApi.getArchiveHistory(
        id,
        fileId,
        archiveId
      );

      if (code === 200) {
        const createdBy = {
          id: data?.created_by_user?.id,
          name: data?.created_by_user?.name,
          subLabel: `${
            language === "en"
              ? data?.created_by_user?.user_detail?.department_selective
                  ?.name_en
              : data?.created_by_user?.user_detail?.department_selective
                  ?.name_ar
          } (${
            language === "en"
              ? data?.created_by_user?.user_detail?.office_location_selective
                  ?.name_en
              : data?.created_by_user?.user_detail?.office_location_selective
                  ?.name_ar
          })`,
          avatar: data?.created_by_user?.profile
            ? `${ASSET_URL}/${data?.created_by_user?.profile}`
            : PlaceholderImg,
          date: getFormattedDate(data?.created_at),
          time: getFormattedTime(data?.created_at),
        };

        const editHistory =
          data?.history_update?.length > 0 &&
          data?.history_update?.map((user) => ({
            id: user?.updated_by?.id,
            name: user?.updated_by?.name,
            subLabel: `${
              language === "en"
                ? user.updated_by.user_detail.department_selective.name_en
                : user.updated_by.user_detail.department_selective.name_ar
            } (${
              language === "en"
                ? user.updated_by.user_detail.office_location_selective.name_en
                : user.updated_by.user_detail.office_location_selective.name_ar
            })`,
            avatar: user?.updated_by?.profile
              ? `${ASSET_URL}/${user?.updated_by?.profile}`
              : PlaceholderImg,
            date: getFormattedDate(user.created_at),
            time: getFormattedTime(user.created_at),
          }));

        const transferHistory =
          data?.transfer_histories.length > 0 &&
          data?.transfer_histories.map((transUser) => ({
            from: {
              id: transUser?.transfer_by?.id,
              name: transUser?.transfer_by?.name,
              subLabel: `${
                language === "en"
                  ? transUser.transfer_by?.user_detail?.department_selective
                      ?.name_en
                  : transUser.transfer_by?.user_detail?.department_selective
                      ?.name_ar
              } (${
                language === "en"
                  ? transUser?.transfer_by?.user_detail
                      ?.office_location_selective?.name_en
                  : transUser?.transfer_by?.user_detail
                      ?.office_location_selective?.name_ar
              })`,
              avatar: transUser?.transfer_by?.profile
                ? `${ASSET_URL}/${transUser?.transfer_by?.profile}`
                : PlaceholderImg,
            },
            to: {
              id: transUser?.transfer_to?.id,
              name: transUser?.transfer_to?.name,
              subLabel: `${
                language === "en"
                  ? transUser.transfer_to?.user_detail?.department_selective
                      ?.name_en
                  : transUser.transfer_to?.user_detail?.department_selective
                      ?.name_ar
              } (${
                language === "en"
                  ? transUser?.transfer_to?.user_detail
                      ?.office_location_selective?.name_en
                  : transUser?.transfer_to?.user_detail
                      ?.office_location_selective?.name_ar
              })`,
              avatar: transUser?.transfer_to?.profile
                ? `${ASSET_URL}/${transUser?.transfer_to?.profile}`
                : PlaceholderImg,
            },
            date: getFormattedDate(transUser.created_at),
            time: getFormattedTime(transUser.created_at),
          }));

        setHistoryData((prevData) => ({
          ...prevData,
          createdBy: createdBy,
          editHistory: editHistory,
          transferHistory: transferHistory,
        }));
      }
    } catch (error) {
    } finally {
      setHistoryLoading(false);
    }
  };
  const onArchiveTransfer = async () => {
    const selectedRadio = document.querySelector(
      'input[name="trans_user_select"]:checked'
    );
    const selectedId = selectedRadio ? parseInt(selectedRadio.value, 10) : null;
    const updatedData = {
      archive_id: archiveId,
      sent_to: selectedId,
    };
    try {
      setTransferLoading(true);
      const response = await CabinetsApi.transferArchive(
        id,
        fileId,
        updatedData
      );
      if (response.code === 200) {
        toast.success(`${t("archive")} ${t("Transfer")} ${t("Successfully")}`);
        // onHide();
        setShowTransferModal(false);
        setCurrentStep(0);
        setTransferSelections(Array(stepsToTransfer.length).fill(null));
        getCabinetsList();
        // navigate("/cabinets")
      }
      if (response.code === 100 || response.code === 204) {
        toast.error(response.message);
      }
    } catch (error) {
    } finally {
      setTransferLoading(false);
    }
  };
  return (
    <Box>
      <Box className="mx-4 mt-4">
        <SubHeadingStyled size="16px" weight="700" color="var(--text)">
          {t("archives")}
        </SubHeadingStyled>
      </Box>
      <Box>
        <ListTable
          options={["view", "info", "edit", "transfer", "status", "delete"]}
          typeId={3}
          rows={rowsData}
          columns={filteredColumns}
          onViewDetails={onViewDetails}
          onInfoView={handleInfo}
          onEdit={handleEdit}
          activePage={1}
          perPage={10}
          onDelete={onDelete}
          onStatusToggle={handleStatusToggle}
          onTransfer={handleTransfer}
        />
      </Box>

      <HistoryModal
        title={t("ArchiveHistory")}
        show={showHistoryModal}
        onHide={() => setShowHistoryModal(false)}
        createdBy={historyData.createdBy}
        editHistory={historyData.editHistory}
        transferHistory={historyData.transferHistory}
        historyLoading={historyLoading}
      />

      <TransferModal
        onSubmit={onArchiveTransfer}
        show={showTransferModal}
        onHide={() => setShowTransferModal(false)}
        transferLoading={transferLoading}
        selections={transferSelections}
        setSelections={setTransferSelections}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        // handleTransIds={handleTransIds} // Function to capture selected member IDs
        // cabinetId={cabinetId} // Pass cabinet ID to modal
      />
      <DeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        title={t("Archive")}
        onDelete={handleDelete}
      />
      <RequestModal
        title={"Archive"}
        show={showRequestModal}
        onHide={() => setShowRequestModal(false)}
        handleRequest={handleRequest}
        isLoading={requestLoading}
      />
    </Box>
  );
};

export default ArchivesList;
